/**
 * AmlSidebar — T2 sidebar for the AML Compliance module.
 *
 * Shows AML sections, permission-gated:
 *  - Customers (aml.customers.view)
 *  - Transactions (aml.transactions.view)
 *  - Reports (aml.reports.view)
 *  - Pivot Report (aml.reports.view)
 *  - OFAC Screening (aml.sanctions.view)
 *  - Clients (aml.clients.manage — super admin only) [hidden]
 *  - Users (aml.users.manage — client admin+)
 */
import React from 'react';
import UsersIcon from 'lucide-react/dist/esm/icons/users';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import ArrowLeftRight from 'lucide-react/dist/esm/icons/arrow-left-right';
import PieChart from 'lucide-react/dist/esm/icons/pie-chart';
import Shield from 'lucide-react/dist/esm/icons/shield';
import type { LucideIcon } from 'lucide-react';
import { useUIStore } from '../../../stores/uiStore';
import { usePermissions } from '../../../hooks/usePermissions';
import { Permissions } from '../../../config/permissions';
import { getTranslation } from '../../../i18n';
import type { AmlSection } from '../types/aml';

interface SectionItem {
  id: AmlSection;
  labelKey: string;
  descriptionKey: string;
  icon: LucideIcon;
  permission?: string;
}

const SECTIONS: SectionItem[] = [
  {
    id: 'customers',
    labelKey: 'amlNavCustomers',
    descriptionKey: 'amlNavCustomersDesc',
    icon: UsersIcon,
    permission: Permissions.AML_CUSTOMERS_VIEW,
  },
  {
    id: 'transactions',
    labelKey: 'amlNavTransactions',
    descriptionKey: 'amlNavTransactionsDesc',
    icon: ArrowLeftRight,
    permission: Permissions.AML_TRANSACTIONS_VIEW,
  },
  {
    id: 'reports',
    labelKey: 'amlNavTransactionReport',
    descriptionKey: 'amlNavTransactionReportDesc',
    icon: FileText,
    permission: Permissions.AML_REPORTS_VIEW,
  },
  {
    id: 'pivot-report',
    labelKey: 'amlNavTransactionPivotReport',
    descriptionKey: 'amlNavTransactionPivotReportDesc',
    icon: PieChart,
    permission: Permissions.AML_REPORTS_VIEW,
  },
  {
    id: 'sanctions',
    labelKey: 'amlNavSanctions',
    descriptionKey: 'amlNavSanctionsDesc',
    icon: Shield,
    permission: Permissions.AML_SANCTIONS_VIEW,
  },
  // TODO: re-enable clients section when ready
  // {
  //   id: 'clients',
  //   labelKey: 'amlNavClients',
  //   descriptionKey: 'amlNavClientsDesc',
  //   icon: Building,
  //   permission: Permissions.AML_CLIENTS_MANAGE,
  // },
];

const AmlSidebar: React.FC = () => {
  const { amlSection, setAmlSection, language } = useUIStore();
  const { hasPermission } = usePermissions();
  const t = (key: string) => getTranslation(language, key);

  const visibleSections = SECTIONS.filter(
    (s) => !s.permission || hasPermission(s.permission as any),
  );

  return (
    <aside className="fixed top-[60px] left-[60px] w-[240px] h-[calc(100vh-60px)] bg-surface-page border-r border-subtle z-sticky flex flex-col transition-all duration-300">
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-subtle">
        <h2 className="text-xs font-semibold text-content-tertiary uppercase tracking-wider">
          {t('amlSidebarTitle')}
        </h2>
      </div>

      {/* Section Navigation */}
      <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
        {visibleSections.map((section) => {
          const isActive = amlSection === section.id;
          const Icon = section.icon;

          return (
            <button
              key={section.id}
              onClick={() => setAmlSection(section.id)}
              className={`
                w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left
                transition-all duration-150 border-none outline-none cursor-pointer
                ${isActive
                  ? 'bg-status-success/10 text-status-success'
                  : 'text-content-tertiary hover:text-content-secondary hover:bg-surface-primary'
                }
              `}
            >
              <div
                className={`
                  w-8 h-8 rounded-lg flex items-center justify-center shrink-0
                  ${isActive ? 'bg-status-success/15' : 'bg-surface-primary'}
                `}
              >
                <Icon
                  size={16}
                  strokeWidth={isActive ? 2.2 : 1.8}
                  className={isActive ? 'text-status-success' : 'text-content-muted'}
                />
              </div>
              <div className="min-w-0">
                <span className={`block text-sm font-medium ${isActive ? 'text-status-success' : 'text-content-secondary'}`}>
                  {t(section.labelKey)}
                </span>
                <span className="block text-[11px] text-content-muted mt-0.5 truncate">
                  {t(section.descriptionKey)}
                </span>
              </div>
              {isActive && (
                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-status-success shrink-0" />
              )}
            </button>
          );
        })}
      </nav>
    </aside>
  );
};

export default React.memo(AmlSidebar);
